import type { ReactNode } from 'react';
import { useState } from 'react';
import { McButton, McTextarea } from '@maersk-global/mds-react-wrapper';

interface EditableTextProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  label?: string;
  renderDisplay?: (value: string) => ReactNode;
}

export const EditableText = ({ value, onSave, label = 'Description', renderDisplay }: EditableTextProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const handleEdit = () => {
    setDraft(value);
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <McTextarea
          label={label}
          value={draft}
          rows={6}
          width="full"
          input={(e: CustomEvent) => setDraft((e.target as HTMLTextAreaElement).value)}
        />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <McButton
            label="Save"
            appearance="primary"
            fit="small"
            disabled={saving}
            click={handleSave}
          />
          <McButton
            label="Cancel"
            appearance="neutral"
            fit="small"
            disabled={saving}
            click={handleCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
      <div style={{ flex: 1 }}>
        {renderDisplay ? renderDisplay(value) : <p style={{ margin: 0, lineHeight: 1.6 }}>{value}</p>}
      </div>
      <McButton
        label="Edit"
        hiddenlabel
        icon="pencil"
        appearance="neutral"
        variant="plain"
        fit="small"
        click={handleEdit}
      />
    </div>
  );
};
